import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import QRCode from 'qrcode';
import api from '../utils/api';

/**
 * "Allow phones on this network": the desktop app's LAN switch, and the
 * address a phone on the same Wi-Fi opens PITRIOT at.
 *
 * A phone uploads its photos to the desktop backend through signed /objects
 * PUTs, so it has to reach this computer over the LAN. The switch and the
 * addresses come from the desktop server itself (desktop/http_server.py,
 * GET/PUT /api/desktop/network, which needs a session; switching is for
 * collection managers, `can_toggle`). The cloud API has no such route and
 * answers 404: then -- and on any other failure -- nothing is shown at all,
 * so the cloud app never offers a switch that does nothing.
 *
 * A QR code is drawn only for an address the server is listening on: a code
 * that opens nothing is worse than none.
 *
 * Props:
 *   wrap(children)  optional: how the panel is framed once there is something
 *                   to show (a settings page's section); never called otherwise
 */
export function lanAppUrl(ip, port) {
  return `http://${ip}:${port}/`;
}

export default function LanAccessPanel({ wrap = (children) => children }) {
  const { t } = useTranslation('account');
  const [info, setInfo] = useState(null);
  const [selectedIp, setSelectedIp] = useState('');
  const [qr, setQr] = useState('');
  const [toggling, setToggling] = useState(false);
  const [toggleError, setToggleError] = useState(false);

  const load = useCallback(() => {
    let live = true;
    api.get('/desktop/network')
      .then((res) => { if (live) setInfo(res?.data?.desktop === true ? res.data : null); })
      .catch(() => { if (live) setInfo(null); });
    return () => { live = false; };
  }, []);
  useEffect(() => load(), [load]);

  const reachable = info?.lan_enabled ? (info.listening || []) : [];
  const ip = reachable.includes(selectedIp) ? selectedIp : (reachable[0] || '');
  const url = ip ? lanAppUrl(ip, info.port) : '';

  useEffect(() => {
    if (!url) { setQr(''); return undefined; }
    let cancelled = false;
    QRCode.toDataURL(url, { width: 200, margin: 1 })
      .then((data) => { if (!cancelled) setQr(data); })
      .catch(() => { if (!cancelled) setQr(''); });
    return () => { cancelled = true; };
  }, [url]);

  if (!info) return null;

  const toggle = async () => {
    setToggling(true);
    setToggleError(false);
    try {
      const { data } = await api.put('/desktop/network', { lan_enabled: !info.lan_enabled });
      if (data?.desktop === true) setInfo(data);
    } catch {
      setToggleError(true);
    } finally {
      setToggling(false);
    }
  };

  const bindErrors = Object.keys(info.errors || {});

  return wrap(
    <div data-testid="lan-access">
      <p style={styles.hint}>{t('settings.lan.hint')}</p>
      <label style={styles.switchRow}>
        <input type="checkbox" checked={!!info.lan_enabled} onChange={toggle}
          disabled={!info.can_toggle || toggling} title={t('tip.lanToggle')} data-testid="lan-toggle" />
        <span>{t('settings.lan.allow')}</span>
      </label>
      <p style={{ ...styles.status, color: info.lan_enabled ? 'var(--c-success-2-fg)' : 'var(--c-warning-text-fg)' }}
        data-testid="lan-status">
        {info.lan_enabled ? t('settings.lan.on') : t('settings.lan.off')}
      </p>
      {!info.can_toggle && !info.lan_enabled && <p style={styles.hint}>{t('settings.lan.askManager')}</p>}
      {toggleError && <p role="alert" style={styles.error}>{t('settings.lan.toggleFailed')}</p>}
      {info.lan_enabled && bindErrors.length > 0 && (
        <p style={styles.error}>{t('settings.lan.bindFailed', { addresses: bindErrors.join(', ') })}</p>
      )}
      {info.lan_enabled && reachable.length === 0 && bindErrors.length === 0 && (
        <p style={styles.hint}>{t('settings.lan.noAddress')}</p>
      )}
      {url && (
        <div style={{ marginTop: 10 }}>
          {reachable.length > 1 && (
            <label style={{ display: 'block', fontSize: 13, marginBottom: 6 }}>
              {t('settings.lan.addressLabel')}{' '}
              <select value={ip} onChange={(e) => setSelectedIp(e.target.value)} dir="ltr" title={t('tip.lanAddress')}>
                {reachable.map((a) => <option key={a} value={a}>{a}</option>)}
              </select>
            </label>
          )}
          <div style={{ fontSize: 13, color: 'var(--c-text-muted-fg)' }}>{t('settings.lan.urlLabel')}</div>
          <code dir="ltr" style={styles.code} data-testid="lan-url">{url}</code>
          {qr && <img src={qr} alt={t('settings.lan.qrAlt')} width={200} height={200} style={styles.qr} />}
          <p style={styles.hint}>{t('settings.lan.firewall')}</p>
        </div>
      )}
    </div>,
  );
}

const styles = {
  hint: { fontSize: 13, color: 'var(--c-text-muted-fg)', margin: '0 0 8px' },
  switchRow: { display: 'flex', alignItems: 'center', gap: 8, fontSize: 14, fontWeight: 600, cursor: 'pointer' },
  status: { fontSize: 13, fontWeight: 600, margin: '6px 0' },
  error: { fontSize: 13, color: 'var(--c-danger-fg)', background: 'var(--c-danger-soft)', borderRadius: 6,
           padding: '8px 12px', margin: '6px 0' },
  code: { display: 'block', marginTop: 4, fontSize: 13, background: 'var(--c-bg-muted)', borderRadius: 4,
          padding: '6px 8px', wordBreak: 'break-all', userSelect: 'all', color: 'var(--c-text-fg)' },
  qr: { display: 'block', marginTop: 10, maxWidth: '100%', height: 'auto' },
};
